import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CancelOutlined as RejectIcon } from '@mui/icons-material';
import DialogHeader from '../common/DialogHeader';
import { rejectPickupOrder } from '../../services/order-approval.service';
import { formatKg } from '../../utils/formatters';

export interface RejectOrderTarget {
  pickupOrderId: string;
  pointName: string | null;
  supplierName: string | null;
  weightKg: number | null;
}

interface Props {
  open: boolean;
  organizationId: string;
  target: RejectOrderTarget | null;
  onClose: () => void;
  onRejected: () => void;
}

/**
 * The facility manager's refusal of a delivered order. A justification is required: it
 * is the one thing the delivering organization gets to see about the decision.
 */
export default function RejectOrderDialog({ open, organizationId, target, onClose, onRejected }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open, target]);

  const trimmed = reason.trim();

  const submit = async () => {
    if (!target || !trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await rejectPickupOrder(organizationId, target.pickupOrderId, trimmed);
      onRejected();
      onClose();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.detail ?? t('rejectDialog.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogHeader title={t('rejectDialog.title')} subtitle={target?.supplierName ?? target?.pointName ?? undefined} />
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('rejectDialog.intro', { weight: formatKg(target?.weightKg, locale) })}
          </Typography>
          <TextField
            label={t('rejectDialog.reasonLabel')}
            fullWidth
            multiline
            minRows={3}
            required
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText={t('rejectDialog.reasonHelp')}
            inputProps={{ maxLength: 2000, 'data-testid': 'reject-reason' }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>{t('transportation.routes.form.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RejectIcon />}
          disabled={busy || !trimmed}
          onClick={() => void submit()}
        >
          {t('rejectDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
